import type { LaborRangePreset } from '@/components/payroll/laborAnalyticsControls'
import {
  LABOR_STORE_NAMES,
  laborStationCacheToken,
  laborStoreCacheToken,
  type LaborChartMetric,
  type LaborChartQuery,
  type LaborChartResponse,
  type LaborPoint,
  type LaborRefreshResponse,
  type LaborSeries,
  type LaborTimescale,
} from '@/services/laborAnalytics'

const REQUIRED_LABOR_CHARTS: LaborChartMetric[] = [
  'gross_sales_per_hour',
  'regular_hours_by_station',
  'overtime_hours_by_station',
  'wages_by_station',
  'avg_hourly_wages',
  'wages_pct_of_sales',
  'wages_pct_of_station_sales',
]

function hasRequiredCharts(
  charts: Record<LaborChartMetric, LaborChartResponse> | null | undefined,
): boolean {
  if (charts == null || typeof charts !== 'object') return false
  return REQUIRED_LABOR_CHARTS.every((metric) => {
    const chart = charts[metric]
    return chart != null && Array.isArray(chart.series)
  })
}

export function isLaborAnalyticsCachePayloadCurrent(
  payload: LaborRefreshResponse | null | undefined,
): boolean {
  if (payload?.charts_by_store == null || typeof payload.charts_by_store !== 'object') {
    return false
  }
  return LABOR_STORE_NAMES.every((store) =>
    hasRequiredCharts(payload.charts_by_store?.[store]),
  )
}

export const LABOR_ANALYTICS_CACHE_KEY = 'bookkeeper:labor-analytics-cache'
export const LABOR_ANALYTICS_CACHE_TTL_MS = 24 * 60 * 60 * 1000
const PERSIST_SOFT_CAP_CHARS = 4_500_000

export interface LaborAnalyticsCacheQuery {
  stores?: string[]
  stations?: string[]
  store?: string
  station?: string
  start: string
  end: string
  timescale?: LaborTimescale
}

export interface LaborAnalyticsCacheEntry {
  savedAt: number
  rangePreset: LaborRangePreset
  query: LaborAnalyticsCacheQuery
  payload: LaborRefreshResponse
}

interface LaborAnalyticsCacheStore {
  lastKey: string
  entries: Record<string, LaborAnalyticsCacheEntry>
}

type CompactLaborSeries = {
  key: string
  start: string
  v: (string | null)[]
  n?: (string | null)[]
  d?: (string | null)[]
}

type CompactLaborChartResponse = Omit<LaborChartResponse, 'series'> & {
  series: (CompactLaborSeries | LaborSeries)[]
}

type PersistedLaborRefreshPayload = Omit<LaborRefreshResponse, 'charts' | 'charts_by_store'> & {
  charts_by_store?: Record<string, Record<LaborChartMetric, CompactLaborChartResponse>>
}

type PersistedLaborAnalyticsCacheEntry = Omit<LaborAnalyticsCacheEntry, 'payload'> & {
  payload: PersistedLaborRefreshPayload
}

let memoryStore: LaborAnalyticsCacheStore | null = null

export function laborAnalyticsCacheKey(query: LaborChartQuery | LaborAnalyticsCacheQuery): string {
  return [
    laborStoreCacheToken(query.stores ?? query.store),
    laborStationCacheToken(query.stations ?? query.station),
    query.start,
    query.end,
  ].join('|')
}

export function isLaborAnalyticsCacheValid(savedAt: number, now = Date.now()): boolean {
  return now - savedAt < LABOR_ANALYTICS_CACHE_TTL_MS
}

export function isQuotaExceededError(error: unknown): boolean {
  if (error == null) return false
  if (typeof error === 'object') {
    const candidate = error as { name?: string; code?: number; message?: string }
    if (
      candidate.name === 'QuotaExceededError' ||
      candidate.name === 'NS_ERROR_DOM_QUOTA_REACHED' ||
      candidate.code === 22 ||
      candidate.code === 1014
    ) {
      return true
    }
    const message = candidate.message ?? ''
    if (/quota|exceeded/i.test(message)) return true
  }
  const text = String(error)
  return /quota|exceeded/i.test(text)
}

function parseIsoDateUtc(iso: string): number {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso)
  if (!match) throw new Error(`Invalid ISO date: ${iso}`)
  return Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
}

function formatIsoDateUtc(ms: number): string {
  const date = new Date(ms)
  const year = date.getUTCFullYear()
  const month = String(date.getUTCMonth() + 1).padStart(2, '0')
  const day = String(date.getUTCDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function addIsoDays(iso: string, days: number): string {
  return formatIsoDateUtc(parseIsoDateUtc(iso) + days * 86_400_000)
}

function isLegacyChartSeries(series: CompactLaborSeries | LaborSeries): series is LaborSeries {
  return Array.isArray((series as LaborSeries).points)
}

function omitNullPointFields(point: LaborPoint): LaborPoint {
  const next: LaborPoint = { date: point.date }
  if (point.value != null) next.value = point.value
  if (point.numerator != null) next.numerator = point.numerator
  if (point.denominator != null) next.denominator = point.denominator
  return next
}

function pointsAreConsecutiveDaily(points: LaborPoint[]): boolean {
  if (points.length <= 1) return true
  const sorted = [...points].sort((a, b) => a.date.localeCompare(b.date))
  for (let index = 1; index < sorted.length; index += 1) {
    const expected = addIsoDays(sorted[index - 1]!.date, 1)
    if (sorted[index]!.date !== expected) return false
  }
  return true
}

function compactSeries(series: LaborSeries): CompactLaborSeries | LaborSeries {
  const sorted = [...series.points].sort((a, b) => a.date.localeCompare(b.date))
  if (sorted.length === 0) {
    return { key: series.key, points: [] }
  }
  if (!pointsAreConsecutiveDaily(sorted)) {
    return { key: series.key, points: sorted.map(omitNullPointFields) }
  }

  const hasRatio = sorted.some(
    (point) =>
      point.numerator != null &&
      point.numerator !== '' &&
      point.denominator != null &&
      point.denominator !== '',
  )
  const compact: CompactLaborSeries = {
    key: series.key,
    start: sorted[0]!.date,
    v: sorted.map((point) => point.value ?? null),
  }
  if (hasRatio) {
    compact.n = sorted.map((point) => point.numerator ?? null)
    compact.d = sorted.map((point) => point.denominator ?? null)
  }
  return compact
}

function expandSeries(series: CompactLaborSeries | LaborSeries): LaborSeries {
  if (isLegacyChartSeries(series)) {
    return series
  }
  const points: LaborPoint[] = []
  let current = series.start
  for (let index = 0; index < series.v.length; index += 1) {
    const point: LaborPoint = { date: current, value: series.v[index] ?? null }
    if (series.n) point.numerator = series.n[index] ?? null
    if (series.d) point.denominator = series.d[index] ?? null
    points.push(point)
    current = addIsoDays(current, 1)
  }
  return { key: series.key, points }
}

function compactChart(chart: LaborChartResponse): CompactLaborChartResponse {
  return {
    ...chart,
    series: chart.series.map(compactSeries),
  }
}

function expandChart(chart: CompactLaborChartResponse): LaborChartResponse {
  return {
    ...chart,
    series: chart.series.map(expandSeries),
  }
}

function compactPayload(payload: LaborRefreshResponse): PersistedLaborRefreshPayload {
  const chartsByStore: Record<string, Record<LaborChartMetric, CompactLaborChartResponse>> = {}
  for (const [store, charts] of Object.entries(payload.charts_by_store ?? {})) {
    const compactStore = {} as Record<LaborChartMetric, CompactLaborChartResponse>
    for (const metric of Object.keys(charts) as LaborChartMetric[]) {
      const chart = charts[metric]
      if (chart) compactStore[metric] = compactChart(chart)
    }
    chartsByStore[store] = compactStore
  }
  const rest = { ...payload }
  delete rest.charts
  delete rest.charts_by_store
  return { ...rest, charts_by_store: chartsByStore }
}

function expandPayload(payload: PersistedLaborRefreshPayload | LaborRefreshResponse): LaborRefreshResponse {
  if (!payload.charts_by_store) {
    return payload as LaborRefreshResponse
  }

  const firstStore = Object.values(payload.charts_by_store)[0]
  const firstChart = firstStore ? Object.values(firstStore)[0] : undefined
  const firstSeries = firstChart?.series[0]
  if (firstSeries && isLegacyChartSeries(firstSeries)) {
    return payload as LaborRefreshResponse
  }

  const chartsByStore: Record<string, Record<LaborChartMetric, LaborChartResponse>> = {}
  for (const [store, charts] of Object.entries(payload.charts_by_store)) {
    const expandedStore = {} as Record<LaborChartMetric, LaborChartResponse>
    for (const metric of Object.keys(charts) as LaborChartMetric[]) {
      const chart = charts[metric]
      if (chart) expandedStore[metric] = expandChart(chart)
    }
    chartsByStore[store] = expandedStore
  }
  return { ...payload, charts_by_store: chartsByStore }
}

function expandCacheEntry(entry: LaborAnalyticsCacheEntry | PersistedLaborAnalyticsCacheEntry): LaborAnalyticsCacheEntry {
  return {
    ...entry,
    payload: expandPayload(entry.payload),
  }
}

function pruneStore(store: LaborAnalyticsCacheStore): LaborAnalyticsCacheStore | null {
  const entries: Record<string, LaborAnalyticsCacheEntry> = {}
  for (const [key, entry] of Object.entries(store.entries)) {
    const expanded = expandCacheEntry(entry)
    if (
      entry?.savedAt &&
      expanded.payload &&
      entry.query &&
      isLaborAnalyticsCacheValid(entry.savedAt) &&
      isLaborAnalyticsCachePayloadCurrent(expanded.payload)
    ) {
      entries[key] = expanded
    }
  }
  if (Object.keys(entries).length === 0) return null
  const lastKey =
    store.lastKey && entries[store.lastKey] ? store.lastKey : Object.keys(entries)[0]!
  return { lastKey, entries }
}

function entriesForRange(
  entries: Record<string, LaborAnalyticsCacheEntry>,
  start: string,
  end: string,
): Record<string, LaborAnalyticsCacheEntry> {
  const next: Record<string, LaborAnalyticsCacheEntry> = {}
  for (const [key, entry] of Object.entries(entries)) {
    if (entry.query.start === start && entry.query.end === end) {
      next[key] = entry
    }
  }
  return next
}

function readPersistedStore(): LaborAnalyticsCacheStore | null {
  try {
    const raw = localStorage.getItem(LABOR_ANALYTICS_CACHE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as {
      lastKey: string
      entries: Record<string, PersistedLaborAnalyticsCacheEntry>
    }
    if (!parsed?.entries || typeof parsed.entries !== 'object') return null
    const pruned = pruneStore(parsed as unknown as LaborAnalyticsCacheStore)
    if (!pruned) {
      localStorage.removeItem(LABOR_ANALYTICS_CACHE_KEY)
      return null
    }
    return pruned
  } catch {
    localStorage.removeItem(LABOR_ANALYTICS_CACHE_KEY)
    return null
  }
}

function readStore(): LaborAnalyticsCacheStore | null {
  if (memoryStore) {
    const pruned = pruneStore(memoryStore)
    if (!pruned) {
      memoryStore = null
      return readPersistedStore()
    }
    memoryStore = pruned
    return pruned
  }
  return readPersistedStore()
}

function persistLocalStorage(store: LaborAnalyticsCacheStore): void {
  const last = store.entries[store.lastKey]
  if (!last) return
  try {
    const persistedEntry: PersistedLaborAnalyticsCacheEntry = {
      ...last,
      payload: compactPayload(last.payload),
    }
    const serialized = JSON.stringify({
      lastKey: store.lastKey,
      entries: { [store.lastKey]: persistedEntry },
    })
    if (serialized.length > PERSIST_SOFT_CAP_CHARS) return
    localStorage.setItem(LABOR_ANALYTICS_CACHE_KEY, serialized)
  } catch (error) {
    if (!isQuotaExceededError(error)) return
  }
}

export function loadLaborAnalyticsCacheEntry(
  query: LaborChartQuery | LaborAnalyticsCacheQuery,
): LaborAnalyticsCacheEntry | null {
  const store = readStore()
  if (!store) return null
  return store.entries[laborAnalyticsCacheKey(query)] ?? null
}

export function hasLaborAnalyticsRangeCache(start: string, end: string): boolean {
  const cache = readStore()
  if (!cache) return false
  return Object.values(cache.entries).some(
    (entry) => entry.query.start === start && entry.query.end === end,
  )
}

export function loadLatestLaborAnalyticsCache(): LaborAnalyticsCacheEntry | null {
  const store = readStore()
  if (!store) return null
  return store.entries[store.lastKey] ?? null
}

export function saveLaborAnalyticsCache(entry: Omit<LaborAnalyticsCacheEntry, 'savedAt'>): LaborAnalyticsCacheEntry {
  const saved: LaborAnalyticsCacheEntry = { ...entry, savedAt: Date.now() }
  const existing = readStore()
  const key = laborAnalyticsCacheKey(entry.query)
  const entries = {
    ...entriesForRange(existing?.entries ?? {}, entry.query.start, entry.query.end),
    [key]: saved,
  }
  const next: LaborAnalyticsCacheStore = { lastKey: key, entries }
  memoryStore = next
  persistLocalStorage(next)
  return saved
}

export function clearLaborAnalyticsCache(): void {
  memoryStore = null
  localStorage.removeItem(LABOR_ANALYTICS_CACHE_KEY)
}
